import React, { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import * as adminService from "@/shared/api/services/adminService";
import * as engineService from "@/shared/api/services/engineService";
import TransactionFilters from "@/shared/components/transactions/TransactionFilters";
import TransactionTable from "@/shared/components/transactions/TransactionTable";
import TransactionDetail from "@/shared/components/transactions/TransactionDetail";
import { exportToCsv } from "@/shared/utils/csvExport";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { auth } from "@/shared/api/graphqlClient";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import { ChevronLeft, ChevronRight, ArrowUpFromLine, CheckCircle2, Clock, Hash, RefreshCw, Loader2, XCircle } from "lucide-react";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

  .wd { display:flex; flex-direction:column; gap:1.25rem; font-family:'Outfit',system-ui,sans-serif; }

  /* header */
  .wd-hdr { display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:0.875rem; }
  .wd-hdr-l { display:flex; align-items:center; gap:0.75rem; }
  .wd-badge { width:2.5rem; height:2.5rem; border-radius:0.75rem; background:rgba(34,211,238,0.10); border:1px solid rgba(34,211,238,0.22); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .wd-badge svg { color:#22D3EE; width:1.1rem; height:1.1rem; }
  .wd-title { font-size:1.375rem; font-weight:800; color:#EFF6FF; letter-spacing:-0.02em; margin:0; }
  .wd-sub { font-size:0.775rem; color:#4D6E90; margin-top:0.1rem; }
  .wd-sub b { color:#93B4D4; font-weight:600; }

  /* stat pills */
  .wd-pills { display:flex; gap:0.5rem; flex-wrap:wrap; }
  .wd-pill { display:inline-flex; align-items:center; gap:0.375rem; padding:0.35rem 0.75rem; border-radius:0.5rem; border:1px solid transparent; font-size:0.775rem; font-weight:600; white-space:nowrap; cursor:default; transition:background 120ms ease, border-color 120ms ease; }
  .wd-pill svg { width:0.75rem; height:0.75rem; }
  .wd-pill-c { background:rgba(34,211,238,0.08); border-color:rgba(34,211,238,0.18); color:#22D3EE; }
  .wd-pill-c:hover { background:rgba(34,211,238,0.13); border-color:rgba(34,211,238,0.30); }
  .wd-pill-s { background:rgba(16,185,129,0.08); border-color:rgba(16,185,129,0.18); color:#34D399; }
  .wd-pill-s:hover { background:rgba(16,185,129,0.13); border-color:rgba(16,185,129,0.30); }
  .wd-pill-p:hover { background:rgba(251,191,36,0.13); border-color:rgba(251,191,36,0.30); }
  .wd-pill-f { background:rgba(239,68,68,0.08); border-color:rgba(239,68,68,0.18); color:#f87171; }
  .wd-pill-f:hover { background:rgba(239,68,68,0.13); border-color:rgba(239,68,68,0.30); }

  /* divider */
  .wd-div { height:1px; background:linear-gradient(90deg,transparent,rgba(34,211,238,0.22) 30%,rgba(251,191,36,0.14) 70%,transparent); border:none; margin:0; }

  /* filter section */
  .wd-filters { background:rgba(15,23,42,0.4); border:1px solid rgba(255,255,255,0.07); border-radius:12px; overflow:hidden; }

  /* table card */
  .wd-table { background:rgba(15,23,42,0.4); border:1px solid rgba(255,255,255,0.07); border-radius:12px; overflow:hidden; transition: opacity 0.3s ease; }
  .wd-table.is-fetching { opacity: 0.6; pointer-events: none; }

  /* pagination */
  .wd-pag { display:flex; align-items:center; justify-content:space-between; background:rgba(15,23,42,0.4); border:1px solid rgba(255,255,255,0.07); border-radius:10px; padding:0.55rem 0.875rem; }
  .wd-pag-info { font-size:0.775rem; color:#4D6E90; }
  .wd-pag-ctrl { display:flex; align-items:center; gap:0.4rem; }
  .wd-pag-lbl { font-size:0.775rem; color:#93B4D4; padding:0 0.2rem; }
  .wd-pag-btn { width:1.875rem; height:1.875rem; border-radius:0.45rem; display:flex; align-items:center; justify-content:center; background:#1e293b; border:1px solid rgba(255,255,255,0.09); color:#93B4D4; cursor:pointer; transition:all 100ms ease; }
  .wd-pag-btn svg { width:0.825rem; height:0.825rem; }
  .wd-pag-btn:hover:not(:disabled) { background:rgba(34,211,238,0.10); border-color:rgba(34,211,238,0.35); color:#22D3EE; }
  .wd-pag-btn:disabled { opacity:0.28; cursor:not-allowed; }
`;

const PAGE_SIZE = 20;
const RESET = {
  search: "",
  status: "all",
  dateFrom: undefined,
  dateTo: undefined
};
const CSV_HEADERS = ["id", "transaction_id", "payment_id", "amount", "status", "currency", "merchant_id", "merchant_name", "bank_code", "bank_account", "account_name", "customer_name", "client_bank_name", "network", "wallet_address", "createdAt", "updatedAt", "type"];

export default function EngineWithdrawals() {
  const { t } = useLanguage();
  const engineId = auth.getUserData()?.userId;

  // 1. Hierarchy Scoping
  const { data: rawResellers = [], isFetching: resellersFetching } = useQuery({
    queryKey: ["engine-hierarchy-resellers", engineId],
    queryFn: async () => {
      const res = await engineService.getMyResellers(1, 1000);
      return res.data || [];
    },
    enabled: !!engineId
  });

  const { data: rawAllMerchants = [], isFetching: merchantsFetching } = useQuery({
    queryKey: ["engine-hierarchy-merchants-all", engineId],
    queryFn: async () => {
      const res = await engineService.getMyMerchants(1, 1000);
      return res.data || [];
    },
    enabled: !!engineId
  });

  const filteredMerchants = useMemo(() => {
    if (!engineId) return [];

    // All Merchants discovery
    const resellerIds = new Set(rawResellers.map(r => r.id));

    // Filter into categories based on creation hierarchy
    const merchantsByEngine = rawAllMerchants.filter(m => m.createdByEngineId === engineId);
    const merchantsByResellers = rawAllMerchants.filter(m => resellerIds.has(m.createdByResellerId));

    // Combine and deduplicate
    const combinedMap = new Map();
    [...merchantsByEngine, ...merchantsByResellers].forEach(m => {
      if (m && m.id) combinedMap.set(m.id, m);
    });

    return Array.from(combinedMap.values());
  }, [rawAllMerchants, rawResellers, engineId]);

  const allowedMerchantIds = useMemo(() => filteredMerchants.map(m => m.id), [filteredMerchants]);

  const { data: profile } = useQuery({
    queryKey: ["engine-profile"],
    queryFn: () => engineService.getProfile(),
  });

  const resellerName = profile?.name || "Engine Partner";

  const [filters, setFilters] = useState({ ...RESET });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(0);

  const filtersMapper = useMemo(() => ({
    status: filters.status && filters.status !== "all" ? filters.status : undefined,
    startDate: filters.dateFrom ? new Date(filters.dateFrom).toISOString() : undefined,
    endDate: filters.dateTo ? new Date(filters.dateTo).toISOString() : undefined,
  }), [filters]);

  const targetMerchantIds = useMemo(() => {
    return allowedMerchantIds.length > 0 ? allowedMerchantIds : ["__NO_SCOPE__"];
  }, [allowedMerchantIds]);


  const { data: withdrawalsData, isLoading: withdrawalsLoading, isFetching: withdrawalsFetching } = useQuery({
    queryKey: ["engine-withdrawals-paginated", page, PAGE_SIZE, filters.search, filtersMapper, targetMerchantIds],
    queryFn: () => adminService.getWithdrawalsPagination(page + 1, PAGE_SIZE, filters.search, filtersMapper, targetMerchantIds),
    enabled: !!engineId && ((filters.merchant && filters.merchant !== "all") || allowedMerchantIds.length > 0),
    keepPreviousData: true,
  });


  const isSyncing = resellersFetching || merchantsFetching || withdrawalsFetching;

  const withdrawals = withdrawalsData?.data || [];
  const totalPages = withdrawalsData?.totalPages || 1;
  const totalItems = withdrawalsData?.total || 0;

  // 5. Normalization for shared components
  const transactions = useMemo(() => {
    return withdrawals.map((w) => ({
      id: w.id,
      transaction_id: w.id,
      payment_id: w.id,
      amount: Number(w.amount ?? 0),
      net_amount: Number(w.net_amount ?? 0),
      total_fee: Number(w.total_fee ?? 0),
      status: (w.status ?? "pending").toLowerCase(),
      currency: "THB",
      merchant_id: w.merchant_id,
      merchant: w.merchants || filteredMerchants.find(m => m.id === w.merchant_id),
      bank_code: w.bank_code,
      bank_account: w.bank_account,
      account_name: w.account_name,
      client_bank_name: w.bank_name || w.bank_code,
      createdAt: w.createdAt,
      type: "WITHDRAWAL",
      _raw: w,
    }));
  }, [withdrawals, filteredMerchants]);

  // 6. Stats Computation
  const stats = useMemo(() => {
    // If we have server-side stats, use them as they represent the overall scoped data
    if (withdrawalsData) {
      return {
        total: withdrawalsData.totalSuccessAmount || 0,
        count: withdrawalsData.total || 0,
        successCount: withdrawalsData.successCount || 0,
        pendingCount: withdrawalsData.pendingCount || 0,
        failedCount: withdrawalsData.failedCount || 0,
        overallValue: withdrawalsData.totalAmount || 0,
      };
    }

    const success = transactions.filter((t) => t.status === "success");
    const pending = transactions.filter((t) => t.status === "pending" || t.status === "processing");
    const failed = transactions.filter((t) => ["failed", "expired", "cancelled", "rejected"].includes(t.status));

    return {
      total: success.reduce((sum, t) => sum + (t.amount || 0), 0),
      count: totalItems,
      successCount: success.length,
      pendingCount: pending.length,
      failedCount: failed.length,
      overallValue: transactions.reduce((sum, t) => sum + (t.amount || 0), 0),
    };
  }, [transactions, totalItems, withdrawalsData]);

  const handleFilter = useCallback((f) => { setFilters(f); setPage(0); }, []);
  const handleReset = useCallback(() => { setFilters(RESET); setPage(0); }, []);
  const handleExport = useCallback(() => {
    exportToCsv(transactions, {
      headers: CSV_HEADERS,
      filename: `engine-withdrawals-${new Date().toISOString().slice(0, 10)}.csv`,
      getCellValue: (r, k) => k === "merchant_name" ? r.merchant?.name : r[k]
    });
  }, [transactions]);

  return (
    <EngineLayout currentPage="EngineWithdrawals" resellerName={resellerName}>
      <style>{CSS}</style>
      <div className="wd">
        {/* Header */}
        <div className="wd-hdr">
          <div className="wd-hdr-l">
            <div className={`wd-badge ${isSyncing ? "animate-pulse" : ""}`}>
              {isSyncing ? <Loader2 className="animate-spin" /> : <ArrowUpFromLine />}
            </div>
            <div>
              <h1 className="wd-title">{t("withdrawals")}</h1>
              <p className="wd-sub">
                <b>{stats.count}</b> {t("recordsFound") || "Records"} ·
                Success: ฿<b>{stats.total.toLocaleString()}</b> ·
                {t("overall") || "Overall"}: ฿<b>{stats.overallValue.toLocaleString()}</b>
              </p>
            </div>
          </div>
          <div className="wd-pills">
            <span className="wd-pill wd-pill-c"><Hash />{stats.count} {t("total") || "Total"}</span>
            <span className="wd-pill wd-pill-s"><CheckCircle2 />{stats.successCount} {t("successful")}</span>
            <span className="wd-pill wd-pill-p"><Clock />{stats.pendingCount} {t("pending")}</span>
            <span className="wd-pill wd-pill-f"><XCircle />{stats.failedCount} {t("declined")}</span>
            {isSyncing && (
              <span className="wd-pill wd-pill-c">
                <RefreshCw className="animate-spin" />
                {t("syncing") || "Syncing..."}
              </span>
            )}
          </div>
        </div>

        <hr className="wd-div" />

        {/* Filters */}
        <div className="wd-filters">
          <TransactionFilters
            filters={filters}
            onFilterChange={handleFilter}
            onExport={handleExport}
            onReset={handleReset}
          />
        </div>

        {/* Table */}
        <div className={`wd-table ${isSyncing && !withdrawalsLoading ? "is-fetching" : ""}`}>
          <TransactionTable
            transactions={transactions}
            isLoading={withdrawalsLoading}
            onView={setSelectedTransaction}
            isQRCode={false}
            mixedTypes={false}
          />
        </div>

        {/* Pagination & Footer */}
        {totalPages > 1 && (
          <div className="wd-pag">
            <span className="wd-pag-info">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalItems)} of {totalItems}</span>
            <div className="wd-pag-ctrl">
              <button className="wd-pag-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}><ChevronLeft /></button>
              <span className="wd-pag-lbl">{page + 1} / {totalPages}</span>
              <button className="wd-pag-btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}><ChevronRight /></button>
            </div>
          </div>
        )}

        {/* Detail Panel */}
        <TransactionDetail
          transaction={selectedTransaction}
          open={!!selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          isQRCode={false}
        />
      </div>
    </EngineLayout>
  );
}